/**
 * CopyableSystem - Wrapper component for AI system/model values
 *
 * Displays a chip icon that shows a styled badge tooltip on hover
 * with the model name and provider color. Matches the UX pattern
 * of CopyableSummary, CopyableId, and CopyableMember.
 *
 * @example
 * ```tsx
 * <CopyableSystem modelId="claude-sonnet-4-6" provider="claude" />
 * ```
 */

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { CopyableSystemProps, TooltipPosition } from './types';
import {
  CopyableValueWrapper,
  CopyableValueTrigger,
  PortalTooltipBubble,
  TooltipRow,
  TooltipStack,
  TooltipLabel,
  TooltipHint,
  SystemPreview,
  SystemBadge,
  SystemColorDot,
} from './styles';
import {
  useTooltipPosition,
  copyToClipboard,
  getIconForVariant,
  getCopyIndicatorIcon,
  getCheckmarkIcon,
} from './utils';

// Canonical codename/color helpers from the leaf config module (2026-07-06:
// switched off the GlobalChatSystem re-export so the wallet page doesn't pull
// the whole chat component in, and onto the provider-aware resolver so wallet
// rows persisted without a model_id show the provider family, not 'unknown').
import { detectProviderFromModelId, getPublicSystemNameForUsage, getProviderColor } from '../../../config/providerCodeNames';

/**
 * Portal Tooltip Component
 * Renders tooltip content in a portal for proper z-index layering
 */
const PortalTooltip: React.FC<{
  visible: boolean;
  position: TooltipPosition;
  variant?: 'default' | 'success';
  maxWidth?: number;
  children: React.ReactNode;
}> = ({ visible, position, variant = 'default', maxWidth, children }) => {
  if (!visible) return null;

  return ReactDOM.createPortal(
    <PortalTooltipBubble
      $visible={visible}
      $position={position}
      $variant={variant}
      $maxWidth={maxWidth}
    >
      {children}
    </PortalTooltipBubble>,
    document.body
  );
};

export const CopyableSystem: React.FC<CopyableSystemProps> = ({
  modelId,
  provider,
  generations,
  onCopy,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const modelName = getPublicSystemNameForUsage(modelId, provider);
  const color = getProviderColor(provider);

  // Multi-model turns (parity plan Phase 0, 2026-08-02): one codename badge
  // per DISTINCT model in call order. Codename resolution keeps the
  // white-label guarantee — raw vendor ids never render. Falls back to the
  // single headline badge when 0-1 distinct models are present.
  const generationBadges = (() => {
    if (!Array.isArray(generations) || generations.length === 0) return null;
    const seen = new Set<string>();
    const badges: Array<{ codeName: string; color: string }> = [];
    for (const g of generations) {
      const model = typeof g?.model === 'string' ? g.model.trim() : '';
      if (!model) continue;
      const generationProvider = detectProviderFromModelId(model);
      const codeName = getPublicSystemNameForUsage(model, generationProvider);
      // Dedupe on the resolved STATION, not the raw id — distinct engine
      // variants of one line collapse into a single station badge.
      if (seen.has(codeName)) continue;
      seen.add(codeName);
      badges.push({
        codeName,
        color: getProviderColor(generationProvider !== 'unknown' ? generationProvider : provider),
      });
    }
    return badges.length > 1 ? badges : null;
  })();

  const copyValue = generationBadges
    ? generationBadges.map((b) => b.codeName).join(' → ')
    : modelName;

  // Calculate tooltip positions
  const mainTooltipPosition = useTooltipPosition(
    triggerRef,
    isHovered && !copied,
    180, // tooltip width
    generationBadges ? 60 + generationBadges.length * 26 : 80, // grows per badge row
    10,  // gap above
    15   // gap below
  );
  const successTooltipPosition = useTooltipPosition(triggerRef, copied, 100, 40, 10, 15);

  // Handle copy action
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!copyValue) return;

    const success = await copyToClipboard(copyValue);
    if (success) {
      setCopied(true);
      onCopy?.(copyValue);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setIsHovered(false);
    setCopied(false);
  };

  return (
    <CopyableValueWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      <CopyableValueTrigger
        ref={triggerRef}
        onClick={handleCopy}
        $copied={copied}
        $variant="system"
        title="click to copy system"
      >
        {getIconForVariant('system')}
        <span className="copy-indicator">
          {getCopyIndicatorIcon()}
        </span>
      </CopyableValueTrigger>

      {/* Main tooltip with system badge */}
      <PortalTooltip
        visible={isHovered && !copied}
        position={mainTooltipPosition}
        maxWidth={180}
      >
        <TooltipStack>
          <TooltipRow $justify="between">
            <TooltipLabel>system</TooltipLabel>
            <TooltipHint>
              {getCopyIndicatorIcon()}
              click
            </TooltipHint>
          </TooltipRow>
          <SystemPreview>
            {generationBadges ? (
              generationBadges.map((badge, index) => (
                <SystemBadge key={`${badge.codeName}-${index}`} $color={badge.color}>
                  <SystemColorDot $color={badge.color} />
                  {badge.codeName}
                </SystemBadge>
              ))
            ) : (
              <SystemBadge $color={color}>
                <SystemColorDot $color={color} />
                {modelName}
              </SystemBadge>
            )}
          </SystemPreview>
        </TooltipStack>
      </PortalTooltip>

      {/* Success tooltip */}
      <PortalTooltip
        visible={copied}
        position={successTooltipPosition}
        variant="success"
        maxWidth={100}
      >
        <TooltipRow $justify="center">
          <span style={{ width: 12, height: 12 }}>
            {getCheckmarkIcon()}
          </span>
          copied!
        </TooltipRow>
      </PortalTooltip>
    </CopyableValueWrapper>
  );
};

export default CopyableSystem;
